import { useEffect } from 'react';
import { useAxiosCmsInstance } from '@gov-nx/api';
import type { LocalizationLanguage } from '@gov-nx/core/app';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useConnection } from '@gov-nx/core/hooks';
import { useTranslationsStore } from '@gov-nx/store';
import { useBoolean } from '@gov-nx/utils/common';
import { useConfiguration } from '../configuration/useConfiguration';
import { initTranslations } from './i18n';

interface TranslationInitProps {
	initialLanguage: LocalizationLanguage;
	onError: (errorMessage: string) => void;
}

export const useTranslationInit = ({
	initialLanguage,
	onError,
}: TranslationInitProps): { translationInitialized: boolean } => {
	const initialized = useBoolean(false);
	const { cmsKeyCache } = useConfiguration();
	const { isOffline } = useConnection();
	const { axiosCmsInstance } = useAxiosCmsInstance();
	const getTranslationsByKey = useTranslationsStore('getTranslationsByKey');
	const updateTranslations = useTranslationsStore('updateTranslations');
	const {
		extraConfig: {
			authorization: { enabled: isAuthorizationEnabled },
		},
	} = useExtraConfigContext();

	useEffect(() => {
		if (initialized.isTrue) {
			return;
		}

		initTranslations(
			initialLanguage,
			axiosCmsInstance,
			cmsKeyCache,
			getTranslationsByKey,
			updateTranslations,
			isAuthorizationEnabled,
			isOffline
		)
			.then(initialized.setTrue)
			.catch((error) => {
				initialized.setFalse();
				onError(`Chyba nacitani lokalizace: ${(error as Error).message}`);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [initialLanguage]);

	return { translationInitialized: initialized.isTrue };
};
